import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Alert, AlertTitle, Button, Stack, TextField, Typography } from "@mui/material";

import { ErrorNotifier } from "../../common/error/ErrorNotifier";
import { useToast } from "../../common/hooks/useToast";
import { Popover } from "../../common/popover/Popover";
import { describeError, getRateLimitRejection } from "../../common/rateLimit";
import { Item } from "../../styles/common";
import { SET_CRM_EMAIL_SEND_PER_RUN } from "./appConfig.mutation";
import { CRM_EMAIL_SEND_SETTINGS } from "./appConfig.queries";
import {
  CrmEmailSendSettings,
  describePace,
  describePerRun,
  describeSource,
  PROPAGATION_NOTICE,
  validatePerRun,
} from "./crmEmailPace";
import { fieldErrorOf } from "./nearbyAlerts";

/**
 * How many campaign emails the CRM sender sends per run, which is all that sets a campaign's pace.
 *
 * Its own component with its own read, like the reCAPTCHA panel, so a failure here renders in this
 * card rather than in place of the screen. A save is confirmed first, because it changes how fast
 * real inboxes are written to, including by a campaign already sending.
 */
export const CrmEmailPacePanel: React.FC = (): JSX.Element => {
  const { data, loading, error } = useQuery<{ crmEmailSendSettings: CrmEmailSendSettings }>(CRM_EMAIL_SEND_SETTINGS, {
    fetchPolicy: "network-only",
  });

  /** The result has no id, so it is written over the query's copy rather than merged. */
  const [save] = useMutation(SET_CRM_EMAIL_SEND_PER_RUN, {
    update(cache, { data: result }) {
      if (!result?.setCrmEmailSendPerRun) return;

      cache.writeQuery({
        query: CRM_EMAIL_SEND_SETTINGS,
        data: { crmEmailSendSettings: result.setCrmEmailSendPerRun },
      });
    },
  });

  const toast = useToast();

  /** `null` until the admin types: the input mirrors the stored number until then. */
  const [draft, setDraft] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const settings = data?.crmEmailSendSettings;
  const value = draft ?? (settings ? String(settings.perRun) : "");
  const perRun = Number(value);
  const valid = Boolean(settings) && !validatePerRun(value, settings?.min, settings?.max);
  const dirty = valid && perRun !== settings?.perRun;

  const handleSaveClick = () => {
    const message = validatePerRun(value, settings?.min, settings?.max);

    if (message) {
      setFieldError(message);
      return;
    }

    setConfirming(true);
  };

  const write = async () => {
    setSaving(true);

    try {
      await save({ variables: { perRun } });
      toast.success(`Campaign emails set to ${perRun.toLocaleString()} per run.`);
      setDraft(null);
      setFieldError("");
    } catch (writeError) {
      const named = fieldErrorOf(writeError);

      if (named) {
        setFieldError(named.message);
      } else if (!getRateLimitRejection(writeError)) {
        // The global Apollo link already announces a rate-limit rejection.
        toast.error(describeError(writeError, "Could not save the number."));
      }
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  };

  return (
    <Item sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Campaign email pace
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        CRM email campaigns go out in batches. Each run of the sender sends at most this many emails, so this number
        alone decides how long a campaign takes. It does not affect test sends or workflows.
      </Typography>

      {error && <ErrorNotifier error={error} />}

      {settings?.warning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {settings.warning}
        </Alert>
      )}

      {settings && !settings.sendingEnabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Campaign sending is turned off on this server, so no campaign goes out at any pace until it is turned on.
        </Alert>
      )}

      {settings && (
        <Alert severity={describePerRun(settings.perRun).severity} sx={{ mb: 2 }}>
          <AlertTitle>In force now: {settings.perRun.toLocaleString()} per run</AlertTitle>
          <Typography variant="body2">
            {describePace(settings.perRun, settings.sendCron)} {describeSource(settings)}
          </Typography>
        </Alert>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        sx={{ mb: 2 }}
      >
        <TextField
          label="Emails per run"
          value={value}
          disabled={saving || loading || !settings}
          error={Boolean(fieldError)}
          helperText={
            fieldError ||
            `A whole number from ${(settings?.min ?? 1).toLocaleString()} to ${(settings?.max ?? 5000).toLocaleString()}.`
          }
          inputProps={{ inputMode: "numeric", "aria-label": "Emails per run" }}
          sx={{ minWidth: 260 }}
          onChange={(event) => {
            setDraft(event.target.value);
            setFieldError("");
          }}
        />

        <Button variant="contained" disabled={!dirty || saving} onClick={handleSaveClick} sx={{ mt: 0.25 }}>
          {saving ? "Saving…" : "Save pace"}
        </Button>
      </Stack>

      {dirty && settings && (
        <Alert severity={describePerRun(perRun).severity} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{describePace(perRun, settings.sendCron)}</strong> {describePerRun(perRun).text}
          </Typography>
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" component="p">
        {PROPAGATION_NOTICE}
      </Typography>

      <Popover open={confirming} handleClose={() => setConfirming(false)}>
        <Item sx={{ p: 3, maxWidth: 520, boxShadow: 6 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Change the pace from {settings?.perRun.toLocaleString()} to {perRun.toLocaleString()} per run?
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            {settings && describePace(perRun, settings.sendCron)} {describePerRun(perRun).text} Any campaign already
            sending changes pace too.
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setConfirming(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" disabled={saving} onClick={write}>
              {saving ? "Saving…" : `Set to ${perRun.toLocaleString()}`}
            </Button>
          </Stack>
        </Item>
      </Popover>
    </Item>
  );
};
